import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Configuration for the force logout behavior
 */
interface ForceLogoutConfig {
    /** Whether to clear URL parameters after processing */
    clearParamsAfterLogout?: boolean;

    /** Debug logging */
    debug?: boolean;
}

/**
 * Parameters that indicate SSO login attempt
 */
const SSO_LOGIN_PARAMS = ['organization', 'connection', 'login_hint', 'default_app'];

/**
 * Hook that monitors URL parameters and forces logout if SSO login parameters
 * indicate a different user/organization should be logged in
 */
export const useForceLogoutOnLoginParams = (config: ForceLogoutConfig = {}) => {
    const { isAuthenticated, isLoading, logout, user, getAccessTokenSilently } = useAuth0();

    const { clearParamsAfterLogout = true, debug = false } = config;

    const hasProcessedRef = useRef(false);

    useEffect(() => {
        // Skip if still loading or already processed
        if (isLoading || hasProcessedRef.current) {
            return;
        }

        // Only check if user is authenticated
        if (!isAuthenticated || !user) {
            return;
        }

        // Parse current URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const hasSsoParams = SSO_LOGIN_PARAMS.some((param) => urlParams.has(param));

        if (debug) {
            console.log('🔍 SSO logout check:', {
                isAuthenticated,
                hasSsoParams,
                currentUser: user?.email,
                urlParams: Object.fromEntries(urlParams.entries()),
            });
        }

        // Only proceed if SSO login parameters are present
        if (!hasSsoParams) {
            hasProcessedRef.current = true;
            return;
        }

        // Check for user/org mismatch
        const checkUserOrgMismatch = async () => {
            try {
                const urlOrganization = urlParams.get('organization');
                const urlLoginHint = urlParams.get('login_hint');
                const urlConnection = urlParams.get('connection');

                // Get current user's organization from token
                let currentUserOrg = null;
                try {
                    const token = await getAccessTokenSilently();
                    const payload = token.split('.')[1];
                    const decodedPayload = JSON.parse(atob(payload));
                    currentUserOrg = decodedPayload.org_id;
                } catch (tokenError) {
                    if (debug) {
                        console.log('🔍 Could not decode token for org check:', tokenError);
                    }
                }

                if (debug) {
                    console.log('🔍 Mismatch check:', {
                        urlOrganization,
                        urlLoginHint,
                        urlConnection,
                        currentUserEmail: user.email,
                        currentUserOrg,
                        currentUserSubject: user.sub,
                    });
                }

                let shouldForceLogout = false;

                // Check organization mismatch
                if (urlOrganization && currentUserOrg && urlOrganization !== currentUserOrg) {
                    shouldForceLogout = true;
                    if (debug) {
                        console.log('🚪 Organization mismatch detected:', {
                            current: currentUserOrg,
                            url: urlOrganization,
                        });
                    }
                }

                // Check email mismatch (if login_hint is provided)
                if (urlLoginHint && user.email && urlLoginHint !== user.email) {
                    shouldForceLogout = true;
                    if (debug) {
                        console.log('🚪 Email mismatch detected:', {
                            current: user.email,
                            url: urlLoginHint,
                        });
                    }
                }

                // Check connection mismatch for SSO users
                if (urlConnection && user.sub) {
                    // Extract connection from user.sub (format: provider|connection|user_id)
                    const subParts = user.sub.split('|');
                    if (subParts.length >= 2) {
                        const currentConnection = subParts[1];
                        if (currentConnection !== urlConnection) {
                            shouldForceLogout = true;
                            if (debug) {
                                console.log('🚪 Connection mismatch detected:', {
                                    current: currentConnection,
                                    url: urlConnection,
                                });
                            }
                        }
                    }
                }

                if (shouldForceLogout) {
                    if (debug) {
                        console.log('🚪 Forcing logout due to user/org mismatch:', {
                            currentUser: user.email,
                            currentOrg: currentUserOrg,
                            urlParams: Object.fromEntries(urlParams.entries()),
                        });
                    }

                    // Mark as processed to prevent multiple logout attempts
                    hasProcessedRef.current = true;

                    // Clear URL parameters if requested
                    if (clearParamsAfterLogout) {
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    }

                    // Force logout and redirect to login with original URL
                    logout({
                        logoutParams: {
                            returnTo:
                                window.location.origin +
                                window.location.pathname +
                                window.location.search,
                        },
                    });

                    return;
                }

                // No mismatch found, mark as processed
                hasProcessedRef.current = true;
            } catch (error) {
                if (debug) {
                    console.error('🚪 Error checking user/org mismatch:', error);
                }
                hasProcessedRef.current = true;
            }
        };

        checkUserOrgMismatch();
    }, [
        isAuthenticated,
        isLoading,
        logout,
        user,
        getAccessTokenSilently,
        clearParamsAfterLogout,
        debug,
    ]);

    // Reset processed flag when authentication state changes
    useEffect(() => {
        if (!isAuthenticated) {
            hasProcessedRef.current = false;
        }
    }, [isAuthenticated]);
};
